import { useState } from 'react';
import { ProofImage } from '../cgOrders/ProofImage';
import { ArtworkDownload } from '../cgOrders/ArtworkDownload';
import { Badge, Button, InlineError, Skeleton } from '../../components/ui';
import {
  reviewErrorMessage,
  useComponentTypes,
  useCreateOption,
  useSubmitOptions,
} from './sampleReview';
import { submissionComponents } from '../artwork/componentVocabulary';
import { useSubmissionOptions } from './sampleReview';
import {
  buildSlots,
  isRoundFull,
  openComponents,
  optionButtonLabel,
  rejectionNotes,
} from './sampleSlots';
import type { ComponentSlot, SlotState } from './sampleSlots';

/**
 * The factory's side of a sample run, component by component.
 *
 * A targeted resubmission is the whole point of this view: after a review,
 * the supplier should be able to see at a glance which components are settled
 * and which ones they have to shoot again, and be physically unable to
 * re-upload the settled ones. Uploading a fifth charm to a slot that was
 * approved a week ago wastes a production run.
 *
 * The wording differs from the reviewer's panel on purpose. "Awaiting review"
 * is TAP's word for work TAP owes; the supplier reads the same state as "with
 * TAP", because from the factory floor it is simply not their move.
 */

const SUPPLIER_SLOT_LABELS: Record<SlotState, string> = {
  locked: 'Approved',
  revision_required: 'New samples needed',
  awaiting_review: 'With TAP',
  // Not "With TAP". TAP can see them, but the factory is still the one
  // holding this component — they may add up to five options before anybody
  // rules on any of them, and a badge saying it is with TAP would tell them
  // the opposite.
  collecting: 'Not sent yet',
  empty: 'Not sent yet',
};

const SUPPLIER_SLOT_TONES: Record<SlotState, 'success' | 'warning' | 'brand' | 'neutral'> = {
  locked: 'success',
  revision_required: 'warning',
  awaiting_review: 'brand',
  collecting: 'neutral',
  empty: 'neutral',
};

/** What the supplier is waiting on, said plainly, for each closed state. */
const CLOSED_REASON: Partial<Record<SlotState, string>> = {
  locked: 'Approved and locked. Nothing more is needed for this component.',
  awaiting_review:
    'Sent to TAP. They will pick one — you can add more once they have reviewed this batch.',
};

/**
 * The line under an OPEN slot that already holds options.
 *
 * Its own message rather than a CLOSED_REASON, because the button is right
 * there: this says what the button is for. A factory that uploads one photo
 * and sees nothing else sends one option per component, which is what
 * happened — TAP had nothing to choose between on any job.
 */
const COLLECTING_HINT =
  'Not sent yet — add up to five options for this component, then send the whole batch to TAP below.';

export function SupplierSampleSlots({
  submissionId,
  canUpload,
}: {
  submissionId: string;
  /**
   * False once TAP has decided the submission. The database refuses the
   * write either way (guard_option_insert), but a control that cannot work
   * should not be offered.
   */
  canUpload: boolean;
}) {
  const components = useComponentTypes();
  const options = useSubmissionOptions(submissionId);
  const create = useCreateOption(submissionId);
  const submit = useSubmitOptions(submissionId);
  const [error, setError] = useState('');
  const [busyComponent, setBusyComponent] = useState('');

  if (components.isPending || options.isPending) {
    return <Skeleton lines={4} label="Loading sample components" />;
  }
  if (components.isError || options.isError) {
    return <InlineError>Sample components could not be loaded.</InlineError>;
  }

  const slots = buildSlots(
    submissionComponents(
      (components.data ?? []).map((component) => component.id),
      (options.data ?? []).map((option) => option.component_type),
    ),
    options.data ?? [],
  );
  const open = new Set(openComponents(slots));

  function upload(componentType: string, file: File) {
    setError('');
    setBusyComponent(componentType);
    create.mutate(
      { componentType, file },
      {
        onError: (cause) => setError(reviewErrorMessage(cause.message)),
        onSettled: () => setBusyComponent(''),
      },
    );
  }

  // Everything photographed and not yet offered. The batch is per SUBMISSION
  // — Kyu named the job as the unit: "they would have to wait til we review
  // before they can submit new versions on the same job order" — so this
  // counts across components rather than per card.
  const staged = (options.data ?? []).filter((option) => option.submitted_at === null).length;

  return (
    <div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {slots.map((slot) => (
        <SupplierSlotCard
          key={slot.component}
          slot={slot}
          canUpload={canUpload && open.has(slot.component)}
          uploading={busyComponent === slot.component}
          onUpload={upload}
        />
      ))}

      {/* The send step, which did not exist before 20261119000200. Uploading
          and submitting were one event, so the first photo went straight to
          TAP and shut the component behind it — a factory could offer exactly
          one option per item and TAP had nothing to choose between.

          Shown only when something is actually staged: a permanent Send
          button with nothing to send is a control that can only fail, and
          this one raises rather than quietly succeeding. */}
      {staged === 0 ? null : (
        <div className="sample-batch-send">
          <p className="sample-option-feedback">
            {staged === 1
              ? '1 option is ready to send.'
              : `${String(staged)} options are ready to send.`}{' '}
            TAP reviews the whole batch at once, and the components close until they have.
          </p>
          <Button
            disabled={!canUpload || submit.isPending}
            onClick={() => {
              setError('');
              submit.mutate(undefined, {
                onError: (cause) => setError(reviewErrorMessage(cause.message)),
              });
            }}
          >
            {submit.isPending ? 'Sending…' : 'Send these samples to TAP'}
          </Button>
        </div>
      )}
    </div>
  );
}

function SupplierSlotCard({
  slot,
  canUpload,
  uploading,
  onUpload,
}: {
  slot: ComponentSlot;
  canUpload: boolean;
  uploading: boolean;
  onUpload: (componentType: string, file: File) => void;
}) {
  // Every denial ever written on this component, not just the newest round.
  // A factory correcting a charm needs the note that killed round 1 as much
  // as the one that killed round 2 — otherwise round 3 repeats round 1.
  const notes = rejectionNotes(slot);
  const closedReason = CLOSED_REASON[slot.state];
  // slot.options is the CURRENT round only, which is exactly what the
  // five-per-round cap counts — so the number on the button is the number the
  // database will assign, including after a revision resets the count.
  const full = isRoundFull(slot.options.length);

  return (
    <div className={`sample-slot sample-slot--${slot.state}`}>
      <div className="sample-slot-header">
        <h4 className="sample-slot-name">{slot.component}</h4>
        <Badge tone={SUPPLIER_SLOT_TONES[slot.state]}>{SUPPLIER_SLOT_LABELS[slot.state]}</Badge>
        {slot.round > 1 ? <span className="sample-slot-round">Round {slot.round}</span> : null}
      </div>

      {slot.options.length === 0 ? (
        <p className="task-panel-text">Nothing uploaded for this component yet.</p>
      ) : (
        <ul className="sample-option-grid">
          {slot.options.map((option, index) => (
            <li
              key={option.id}
              className={`sample-option${option.verdict === 'approved' ? ' sample-option--approved' : ''}${
                option.verdict === 'denied' ? ' sample-option--denied' : ''
              }`}
            >
              {/* ProofImage, never a bare <img>: a Drive share URL is a
                  VIEWER PAGE, not an image, and putting it in src renders the
                  broken icon Kyu screenshotted. Third time this exact bug has
                  appeared — reviewLens.ts documents it, ProofImage fixes it,
                  and this file reintroduced it with a raw img. */}
              <div className="sample-option-image">
                <ProofImage
                  url={option.url}
                  alt={`${slot.component} option ${String(index + 1)}`}
                />
              </div>
              <ArtworkDownload
                url={option.url}
                component={option.label.trim() === '' ? slot.component : option.label}
              />
              <span className="sample-option-label">
                {option.label.trim() === '' ? `Option ${String(index + 1)}` : option.label}
              </span>
              <Badge
                tone={
                  option.verdict === 'approved'
                    ? 'success'
                    : option.verdict === 'denied'
                      ? 'warning'
                      : 'neutral'
                }
              >
                {option.verdict === 'approved'
                  ? 'Chosen'
                  : option.verdict === 'denied'
                    ? 'Not chosen'
                    : 'Pending'}
              </Badge>
              {option.feedback.trim() === '' ? null : (
                <p className="sample-option-feedback">{option.feedback}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {notes.length === 0 ? null : (
        <details className="sample-slot-history" open={slot.state === 'revision_required'}>
          <summary>What TAP said ({notes.length})</summary>
          <ul className="sample-note-list">
            {notes.map((note) => (
              <li key={`${note.label}-${note.feedback}`}>
                <strong>{note.label}:</strong> {note.feedback}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>
        {canUpload && full ? (
          // The cap is enforced in the database (guard_option_insert), so the
          // old button was a control that could only fail. Saying so before
          // the click beats a red error after it.
          <p className="sample-option-feedback">
            Five options is the limit for this round. TAP picks one of these, or sends the component
            back for a new round.
          </p>
        ) : canUpload ? (
          <>
            {slot.state === 'collecting' ? (
              <p className="sample-option-feedback">{COLLECTING_HINT}</p>
            ) : null}
            <label className="ui-button ui-button--secondary ui-button--sm">
              {uploading ? 'Uploading…' : optionButtonLabel(slot.options.length)}
              <input
                type="file"
                accept="image/*"
                aria-label={`Upload a photo for ${slot.component}`}
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file === undefined) return;
                  onUpload(slot.component, file);
                }}
              />
            </label>
          </>
        ) : (
          <p className="sample-option-feedback">
            {closedReason ?? 'This submission is closed for new uploads.'}
          </p>
        )}
      </div>
    </div>
  );
}
